import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectAvailabilityIsLoading,
    selectBasedCalendarOfferIds,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferLevelSelectedDate,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { setOfferNotification } from 'component/page/detail/store/availabilityIndex';
import { Offer } from 'gql/autogen/Offer';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { Detail } from 'gql/autogen/Detail';
import { selectActivityDetail } from 'component/page/detail/store/detail.selectors';
import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { ActivityGqlOperation } from 'store/api/api.types';
import { MeasurementName } from 'core/constant/MeasurementName';
import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { IMeasurementTags } from '@agoda/messaging-client';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { selectOfferLevelCalendarsFromCalendarState, selectSchedulesFromCalendarState } from 'component/page/detail/store/calendar.selectors';
import { getCheapestSchedules, mapPriceNumbers } from 'component/page/detail/PopoverCalendar/PopoverCalendarUtils';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { OfferLevelSchedules } from 'component/page/detail/store/calendar.types';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { logWarn } from 'core-framework/util/LogMessage';

interface StateProps {
    config: BootstrapConfig;
    selectedOfferId: number;
    isLoading: boolean;
    detail: Detail;
    selectedDate: Date;
    offerLevelCalendars: OfferLevelSchedules;
    activityLevelSchedules: AvailabilitySchedule[];
    basedCalendarOfferIds: (number | undefined)[];
    isClickCardExpandOffer?: boolean;
}

interface DispatchProps {
    setNotification: () => void;
    sendAvailabilityOffersMeasurement: (
        pageTypeId: number,
        offerDetailRef?: string,
        offerRefCondition?: string,
        activityId?: number,
        offerId?: number,
        supplierId?: number,
        isEnableOfferGrouping?: boolean,
    ) => void;
}

interface OwnProps {
    offer: Offer;
    index: number;
    supplier: number;
    isUnavailableOffer?: boolean;
    offerLength: number;
}

export interface MergeProps {
    getCalendarPrice: (possibleOffers: EnrichedOfferOption[]) => number;
}

export type Props = StateProps & DispatchProps & OwnProps & MergeProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectAvailabilityIsLoading,
    selectActivityDetail,
    selectOfferLevelSelectedDate,
    selectOfferLevelCalendarsFromCalendarState,
    selectSchedulesFromCalendarState,
    selectBasedCalendarOfferIds,
    (config, selectedOfferId, isLoading, detail, selectedDate, offerLevelCalendars, activityLevelSchedules, basedCalendarOfferIds): StateProps => {
        return {
            config,
            selectedOfferId,
            isLoading,
            detail,
            selectedDate,
            offerLevelCalendars,
            activityLevelSchedules,
            basedCalendarOfferIds,
            isClickCardExpandOffer: isOn(FeatureName.OfferGroupCardExpansion, config),
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setNotification: () => {
        dispatch(setOfferNotification({ [OfferNotificationType.OptionSpec]: false }));
    },
    sendAvailabilityOffersMeasurement: (
        pageTypeId: number,
        offerDetailRef?: string,
        offerRefCondition?: string,
        activityId?: number,
        offerId?: number,
        supplierId?: number,
        isEnableOfferGrouping?: boolean,
    ) => {
        const isOfferRefMatch = offerDetailRef === offerRefCondition;
        const measurementTags: IMeasurementTags = {
            isEnableOfferGrouping: String(isEnableOfferGrouping),
            comparisonOfferDetailRef: isOfferRefMatch ? 'Match' : 'MisMatch',
        };
        const measurement = buildMeasurement(ActivityGqlOperation.availability, MeasurementName.AvailabilityOffersComparison, pageTypeId);
        measurement.tags = {
            ...measurement.tags,
            ...measurementTags,
        };
        sendMeasurement({ ...measurement }, pageTypeId);
        if (!isOfferRefMatch) {
            logWarn(`OfferRef Mismatch for activityId: ${activityId} offerId: ${offerId}`, pageTypeId, {
                ...measurement.tags,
                activityId: String(activityId),
                offerId: String(offerId),
                supplierId: String(supplierId),
            });
        }
    },
});

export const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): Props => {
    const { selectedDate, offerLevelCalendars, activityLevelSchedules, basedCalendarOfferIds } = stateProps;

    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        getCalendarPrice: (possibleOffers: EnrichedOfferOption[]) => {
            const cheapestSchedules = getCheapestSchedules(possibleOffers, activityLevelSchedules, offerLevelCalendars, basedCalendarOfferIds);
            const offerLevelPriceSchedules = mapPriceNumbers(cheapestSchedules);
            const calendarPrice =
                offerLevelPriceSchedules.length > 0
                    ? offerLevelPriceSchedules.find((label) => getDateInYmdFormat(label.date) === getDateInYmdFormat(selectedDate))?.price || offerLevelPriceSchedules[0].price
                    : 0;
            return calendarPrice;
        },
    };
};
